import { MarketingLayout } from '@/components/layout/MarketingLayout';
import { Card } from '@/components/ui/card';
import { SEO } from '@/components/SEO';

export default function PrivacyPage() {
  return (
    <MarketingLayout>
      <SEO
        title="Privacy Policy - Prozpector"
        description="Read Prozpector's privacy policy. Learn how we protect your data and respect your privacy."
        url="https://prozpector.com/privacy"
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-white to-gray-50 py-20 md:py-28">
        <div className="container relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-6">
              Privacy Policy
            </h1>
            <p className="text-xl text-gray-600 mb-4">
              Last updated: November 30, 2025
            </p>
            <p className="text-lg text-gray-600">
              Your privacy is important to us. This policy outlines how Prozpector collects, uses, and protects your information.
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="max-w-4xl mx-auto prose prose-lg">
            <Card className="p-8 md:p-12">
              <div className="space-y-8">
                {/* Introduction */}
                <div>
                  <h2 className="text-2xl font-bold mb-4">1. Introduction</h2>
                  <p className="text-gray-600 mb-4">
                    Prozpector ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our sales automation platform.
                  </p>
                  <p className="text-gray-600">
                    By using Prozpector, you agree to the collection and use of information in accordance with this policy.
                  </p>
                </div>

                {/* Information We Collect */}
                <div>
                  <h2 className="text-2xl font-bold mb-4">2. Information We Collect</h2>

                  <h3 className="text-xl font-semibold mb-3">2.1 Information You Provide</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600 mb-4">
                    <li><strong>Account Information:</strong> Name, email address, company name, and password</li>
                    <li><strong>Payment Information:</strong> Billing address and payment method details (processed securely by Stripe)</li>
                    <li><strong>API Credentials:</strong> Third-party API keys you provide (encrypted and stored securely)</li>
                    <li><strong>Prospect Data:</strong> Information about prospects you import or create within the platform</li>
                    <li><strong>Communications:</strong> Messages you send through our support channels</li>
                  </ul>

                  <h3 className="text-xl font-semibold mb-3">2.2 Information Automatically Collected</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600 mb-4">
                    <li><strong>Usage Data:</strong> Features used, pages visited, and actions taken within the platform</li>
                    <li><strong>Device Information:</strong> IP address, browser type, operating system, and device identifiers</li>
                    <li><strong>Cookies:</strong> Session cookies and persistent cookies for authentication and analytics</li>
                    <li><strong>Log Data:</strong> Server logs including timestamps, errors, and system events</li>
                  </ul>
                </div>

                {/* How We Use Your Information */}
                <div>
                  <h2 className="text-2xl font-bold mb-4">3. How We Use Your Information</h2>
                  <p className="text-gray-600 mb-4">We use collected information for:</p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Providing and maintaining the Prozpector platform</li>
                    <li>Processing your transactions and managing billing</li>
                    <li>Sending email sequences and managing prospect communications on your behalf</li>
                    <li>Providing customer support and responding to inquiries</li>
                    <li>Improving and optimizing our platform through analytics</li>
                    <li>Detecting and preventing fraud, security incidents, and technical issues</li>
                    <li>Sending product updates, feature announcements, and marketing communications (with your consent)</li>
                    <li>Complying with legal obligations and enforcing our Terms of Service</li>
                  </ul>
                </div>

                {/* Data Security */}
                <div>
                  <h2 className="text-2xl font-bold mb-4">4. Data Security & Isolation</h2>
                  <p className="text-gray-600 mb-4">
                    We implement industry-standard security measures to protect your information:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600 mb-6">
                    <li><strong>Row Level Security (RLS):</strong> Complete data isolation between users - you can only access your own prospects, sequences, and templates</li>
                    <li><strong>Multi-Tenant Architecture:</strong> Database-level security policies ensure other users cannot see or access your data</li>
                    <li><strong>Encryption:</strong> AES-256 encryption for data at rest and TLS 1.3 for data in transit</li>
                    <li><strong>API Key Protection:</strong> Your third-party API keys are encrypted and never exposed in logs or UI</li>
                    <li><strong>Access Controls:</strong> Role-based permissions and multi-factor authentication support</li>
                    <li><strong>Infrastructure:</strong> Hosted on Supabase (SOC 2 Type II certified) with automatic backups and disaster recovery</li>
                    <li><strong>Compliance:</strong> SOC 2 Type II compliant and GDPR ready</li>
                    <li><strong>Monitoring:</strong> 24/7 security monitoring and incident response procedures</li>
                  </ul>
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                    <p className="text-sm text-gray-700">
                      <strong>Enterprise-Grade Data Isolation:</strong> Your data is stored securely with Row Level Security (RLS) policies that ensure complete isolation. Other users cannot see or access your prospects, activities, sequences, templates, or any other data - even in the same database. Our team will never access your data without explicit permission.
                    </p>
                  </div>
                </div>

                {/* Data Sharing */}
                <div>
                  <h2 className="text-2xl font-bold mb-4">5. Data Sharing and Disclosure</h2>
                  <p className="text-gray-600 mb-4">
                    We do not sell your personal information. We may share your data with:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li><strong>Service Providers:</strong> Third-party vendors who assist in platform operations (e.g., Stripe for payments, AWS for hosting)</li>
                    <li><strong>API Providers:</strong> Your chosen email and enrichment providers (SendGrid, Apollo, etc.) to execute your campaigns</li>
                    <li><strong>Legal Requirements:</strong> When required by law, court order, or government request</li>
                    <li><strong>Business Transfers:</strong> In connection with mergers, acquisitions, or asset sales (with notice to you)</li>
                    <li><strong>With Your Consent:</strong> When you explicitly authorize us to share information</li>
                  </ul>
                </div>

                {/* Your Rights */}
                <div>
                  <h2 className="text-2xl font-bold mb-4">6. Your Rights</h2>
                  <p className="text-gray-600 mb-4">You have the right to:</p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                    <li><strong>Rectification:</strong> Correct inaccurate or incomplete information</li>
                    <li><strong>Deletion:</strong> Request deletion of your account and associated data</li>
                    <li><strong>Data Portability:</strong> Export your data in a machine-readable format</li>
                    <li><strong>Opt-Out:</strong> Unsubscribe from marketing emails (while still receiving service notifications)</li>
                    <li><strong>Object:</strong> Object to processing of your personal information in certain circumstances</li>
                  </ul>

                  <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded mt-6">
                    <h3 className="font-semibold text-green-900 mb-2">Self-Service Data Controls</h3>
                    <p className="text-sm text-gray-700 mb-3">
                      We provide easy self-service tools for you to exercise your data rights directly from your account:
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                      <li><strong>Data Export:</strong> Download all your data (prospects, activities, templates, sequences) in JSON format from Settings &gt; Security</li>
                      <li><strong>Account Deletion:</strong> Permanently delete your account and all associated data from Settings &gt; Security</li>
                      <li><strong>Notification Preferences:</strong> Control which emails you receive from Settings &gt; Account</li>
                    </ul>
                  </div>

                  <p className="text-gray-600 mt-4">
                    For any additional requests or assistance, contact us at privacy@prozpector.com
                  </p>
                </div>

                {/* Data Retention */}
                <div>
                  <h2 className="text-2xl font-bold mb-4">7. Data Retention</h2>
                  <p className="text-gray-600">
                    We retain your information for as long as your account is active or as needed to provide services. When you delete your account, we permanently delete your data within 30 days, except for:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600 mt-4">
                    <li>Information we're required to retain for legal or compliance purposes</li>
                    <li>Anonymized analytics data that cannot identify you</li>
                    <li>Backup copies (automatically purged after 90 days)</li>
                  </ul>
                </div>

                {/* Cookies */}
                <div>
                  <h2 className="text-2xl font-bold mb-4">8. Cookies and Tracking</h2>
                  <p className="text-gray-600 mb-4">We use cookies for:</p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li><strong>Essential Cookies:</strong> Required for authentication and core platform functionality</li>
                    <li><strong>Analytics Cookies:</strong> To understand how users interact with the platform (Google Analytics)</li>
                    <li><strong>Preference Cookies:</strong> To remember your settings and preferences</li>
                  </ul>
                  <p className="text-gray-600 mt-4">
                    You can control cookies through your browser settings, but disabling essential cookies may impact platform functionality.
                  </p>
                </div>

                {/* International Transfers */}
                <div>
                  <h2 className="text-2xl font-bold mb-4">9. International Data Transfers</h2>
                  <p className="text-gray-600">
                    Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place through:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600 mt-4">
                    <li>Standard Contractual Clauses approved by the European Commission</li>
                    <li>Compliance with GDPR for EU/EEA residents</li>
                    <li>Adherence to applicable data protection laws in each jurisdiction</li>
                  </ul>
                </div>

                {/* Children's Privacy */}
                <div>
                  <h2 className="text-2xl font-bold mb-4">10. Children's Privacy</h2>
                  <p className="text-gray-600">
                    Prozpector is not intended for individuals under 18 years of age. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately at privacy@prozpector.com.
                  </p>
                </div>

                {/* Changes to Policy */}
                <div>
                  <h2 className="text-2xl font-bold mb-4">11. Changes to This Policy</h2>
                  <p className="text-gray-600">
                    We may update this Privacy Policy from time to time. We will notify you of material changes by:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600 mt-4">
                    <li>Posting the updated policy on this page with a new "Last updated" date</li>
                    <li>Sending an email notification to your registered email address</li>
                    <li>Displaying a prominent notice in the platform</li>
                  </ul>
                  <p className="text-gray-600 mt-4">
                    Your continued use of Prozpector after changes constitutes acceptance of the updated policy.
                  </p>
                </div>

                {/* Contact */}
                <div>
                  <h2 className="text-2xl font-bold mb-4">12. Contact Us</h2>
                  <p className="text-gray-600 mb-4">
                    If you have questions about this Privacy Policy or our data practices, contact us:
                  </p>
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <p className="text-gray-600"><strong>Email:</strong> privacy@prozpector.com</p>
                    <p className="text-gray-600"><strong>Address:</strong> Prozpector, Inc., San Francisco, CA, United States</p>
                    <p className="text-gray-600"><strong>Data Protection Officer:</strong> dpo@prozpector.com</p>
                  </div>
                </div>

                {/* GDPR Notice */}
                <div className="border-t pt-8 mt-8">
                  <h2 className="text-2xl font-bold mb-4">GDPR Notice for EU/EEA Residents</h2>
                  <p className="text-gray-600 mb-4">
                    If you are located in the European Union or European Economic Area, you have additional rights under GDPR:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Right to be informed about data processing</li>
                    <li>Right of access to your personal data</li>
                    <li>Right to rectification of inaccurate data</li>
                    <li>Right to erasure ("right to be forgotten")</li>
                    <li>Right to restrict processing</li>
                    <li>Right to data portability</li>
                    <li>Right to object to processing</li>
                    <li>Rights related to automated decision-making</li>
                  </ul>
                  <p className="text-gray-600 mt-4">
                    You also have the right to lodge a complaint with your local data protection authority.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
